<template>
  <div class="gl-page">
    <!-- Header -->
    <div class="gl-page-header">
      <div>
        <h1 class="gl-page-title">Delivery Dashboard</h1>
        <p class="gl-page-sub">Real-time delivery operations overview</p>
      </div>
      <div class="gl-page-header-actions">
        <router-link to="/super-admin/delivery/settings" class="gl-btn-ghost">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 0 0 2.93 19.07M12 2v2m0 18v-2m10-8h-2M4 12H2"/></svg>
          Settings
        </router-link>
        <router-link to="/super-admin/delivery/list" class="gl-btn-primary">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/><rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/></svg>
          View All
        </router-link>
      </div>
    </div>

    <!-- Skeleton Stats -->
    <div v-if="loading" class="gl-stats-grid">
      <div v-for="i in 8" :key="i" class="gl-stat-card">
        <div class="gl-stat-icon-wrap" style="background:#f0f3f2"></div>
        <div style="flex:1">
          <div class="gl-skel gl-skel-sm mb-2"></div>
          <div class="gl-skel gl-skel-lg" style="height:26px"></div>
          <div class="gl-skel gl-skel-md mt-2" style="height:9px"></div>
        </div>
      </div>
    </div>

    <!-- Stats: Today Row -->
    <div v-else>
      <div class="gl-section-label">Today</div>
      <div class="gl-stats-grid">
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#e8f4f1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#900C27" stroke-width="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Deliveries</div>
            <div class="gl-stat-value">{{ stats.delivery_stats?.today?.total || 0 }}</div>
            <div class="gl-stat-sub"><span class="gl-sub-green">{{ stats.delivery_stats?.today?.completed || 0 }} completed</span></div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#fef3e2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#d97706" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">In Progress</div>
            <div class="gl-stat-value" style="color:#d97706">{{ stats.delivery_stats?.today?.in_progress || 0 }}</div>
            <div class="gl-stat-sub"><span class="gl-sub-muted">{{ stats.delivery_stats?.today?.pending || 0 }} pending</span></div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#e6f7f0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Today's Fees</div>
            <div class="gl-stat-value" style="color:#16a34a">RM {{ formatCurrency(stats.revenue_stats?.today_fees) }}</div>
            <div class="gl-stat-sub"><span class="gl-sub-muted">{{ stats.avg_delivery_time_minutes || 0 }} min avg</span></div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#e8f0fd">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Riders Online</div>
            <div class="gl-stat-value" style="color:#2563eb">{{ stats.rider_stats?.available || 0 }}</div>
            <div class="gl-stat-sub"><span class="gl-sub-warn">{{ stats.rider_stats?.busy || 0 }} busy</span></div>
          </div>
        </div>
      </div>

      <div class="gl-section-label" style="margin-top:20px">This Month</div>
      <div class="gl-stats-grid">
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#f0f3f2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#6b7f7b" stroke-width="2"><path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Monthly Deliveries</div>
            <div class="gl-stat-value">{{ stats.delivery_stats?.this_month?.total || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#e6f7f0">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#16a34a" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Monthly Fees</div>
            <div class="gl-stat-value" style="color:#16a34a">RM {{ formatCurrency(stats.revenue_stats?.month_fees) }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#e8f0fd">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#2563eb" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Total Riders</div>
            <div class="gl-stat-value">{{ stats.rider_stats?.total || 0 }}</div>
          </div>
        </div>
        <div class="gl-stat-card">
          <div class="gl-stat-icon-wrap" style="background:#fee2e2">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#dc2626" stroke-width="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
          </div>
          <div class="gl-stat-body">
            <div class="gl-stat-label">Failed Today</div>
            <div class="gl-stat-value" style="color:#dc2626">{{ stats.delivery_stats?.today?.failed || 0 }}</div>
          </div>
        </div>
      </div>
    </div>

    <!-- Bottom row: Provider Breakdown + Settings + Recent Deliveries -->
    <div class="gl-dash-bottom">
      <!-- Left column -->
      <div class="gl-dash-left">
        <!-- Provider Breakdown -->
        <div class="gl-card">
          <div class="gl-card-header">
            <div class="gl-card-title">Provider Breakdown</div>
            <div class="gl-card-sub">This month</div>
          </div>
          <div class="gl-card-body">
            <div v-if="!loading && Object.keys(stats.provider_stats || {}).length">
              <div v-for="(data, provider) in stats.provider_stats" :key="provider" class="gl-provider-row">
                <div class="gl-provider-info">
                  <span class="gl-badge" :class="providerBadgeClass(provider)">{{ provider }}</span>
                  <span class="gl-provider-count">{{ data.count }} deliveries</span>
                </div>
                <div class="gl-provider-fee">RM {{ formatCurrency(data.total_fees) }}</div>
              </div>
            </div>
            <div v-else-if="loading" class="gl-card-skeleton">
              <div v-for="i in 3" :key="i" class="gl-provider-row">
                <div class="gl-skel" style="width:60px;height:10px"></div>
                <div class="gl-skel" style="width:80px;height:10px"></div>
              </div>
            </div>
            <div v-else class="gl-empty-inline">No data this month</div>
          </div>
        </div>

        <!-- Current Settings Summary -->
        <div class="gl-card">
          <div class="gl-card-header">
            <div class="gl-card-title">Current Settings</div>
          </div>
          <div class="gl-card-body">
            <div class="gl-settings-rows">
              <div class="gl-settings-row">
                <span class="gl-settings-key">Fee Method</span>
                <span class="gl-badge gl-badge-info">{{ stats.settings_summary?.fee_method || 'N/A' }}</span>
              </div>
              <div class="gl-settings-row">
                <span class="gl-settings-key">Commission</span>
                <span class="gl-badge gl-badge-green">{{ stats.settings_summary?.commission_type || 'N/A' }}</span>
              </div>
              <div class="gl-settings-row">
                <span class="gl-settings-key">Auto-Assign</span>
                <span class="gl-badge" :class="stats.settings_summary?.auto_assign_enabled ? 'gl-badge-green' : 'gl-badge-gray'">
                  {{ stats.settings_summary?.auto_assign_enabled ? 'Enabled' : 'Disabled' }}
                </span>
              </div>
              <div class="gl-settings-row gl-settings-row-col">
                <span class="gl-settings-key">Providers</span>
                <div class="gl-provider-badges">
                  <span v-for="p in (stats.settings_summary?.enabled_providers || [])" :key="p" class="gl-badge" :class="providerBadgeClass(p)">{{ p }}</span>
                  <span v-if="!stats.settings_summary?.enabled_providers?.length" class="gl-sub-muted">None enabled</span>
                </div>
              </div>
            </div>
            <router-link to="/super-admin/delivery/settings" class="gl-btn-outline-full">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 0 0 2.93 19.07M12 2v2m0 18v-2m10-8h-2M4 12H2"/></svg>
              Manage Settings
            </router-link>
          </div>
        </div>
      </div>

      <!-- Right: Recent Deliveries -->
      <div class="gl-card gl-dash-right">
        <div class="gl-card-header">
          <div class="gl-card-title">Recent Deliveries</div>
          <router-link to="/super-admin/delivery/list" class="gl-btn-primary gl-btn-sm">View All</router-link>
        </div>

        <!-- Skeleton -->
        <template v-if="loading">
          <div class="gl-tbl-skel">
            <div class="gl-tbl-skel-head">
              <div class="gl-skel gl-skel-sm" v-for="i in 7" :key="i"></div>
            </div>
            <div v-for="i in 5" :key="i" class="gl-tbl-skel-row">
              <div class="gl-skel gl-skel-sm" style="flex:0.5"></div>
              <div class="gl-skel gl-skel-md" style="flex:1"></div>
              <div class="gl-skel gl-skel-md" style="flex:1"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel-badge"></div>
              <div class="gl-skel gl-skel-sm" style="flex:0.7"></div>
              <div class="gl-skel gl-skel-sm" style="flex:0.8"></div>
            </div>
          </div>
        </template>

        <div v-else-if="stats.recent_deliveries?.length" class="gl-tbl-scroll">
          <table class="gl-tbl">
            <thead>
              <tr>
                <th>#</th>
                <th>Customer</th>
                <th>Rider</th>
                <th>Provider</th>
                <th>Status</th>
                <th>Fee</th>
                <th>Time</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="d in stats.recent_deliveries" :key="d.id">
                <td><span class="gl-id-badge">#{{ d.id }}</span></td>
                <td>{{ d.customer_name || '—' }}</td>
                <td>{{ d.rider_name || '—' }}</td>
                <td><span class="gl-badge" :class="providerBadgeClass(d.provider)">{{ d.provider }}</span></td>
                <td><span class="gl-badge" :class="statusBadgeClass(d.status)">{{ d.status }}</span></td>
                <td>RM {{ formatCurrency(d.fee) }}</td>
                <td class="gl-tbl-sub">{{ formatDate(d.created_at) }}</td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="gl-empty-inline" style="padding:40px 24px">No recent deliveries</div>
      </div>
    </div>
  </div>
</template>

<script>
import { superAdminDeliveryApi } from '@/services/superAdminApi';

export default {
  name: 'DeliveryDashboard',
  data() {
    return { loading: false, stats: {} };
  },
  created() { this.fetchStats(); },
  methods: {
    async fetchStats() {
      this.loading = true;
      try {
        const r = await superAdminDeliveryApi.getDashboard();
        if (r.data.success) this.stats = r.data.data;
      } catch (e) { console.error(e); }
      finally { this.loading = false; }
    },
    formatCurrency(v) {
      if (!v) return '0.00';
      return parseFloat(v).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    },
    formatDate(d) {
      if (!d) return '—';
      return new Date(d).toLocaleString('en-MY', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
    },
    statusBadgeClass(s) {
      const m = { pending: 'gl-badge-yellow', assigned: 'gl-badge-info', picking_up: 'gl-badge-info', picked_up: 'gl-badge-blue', on_the_way: 'gl-badge-blue', arrived: 'gl-badge-green', delivered: 'gl-badge-green', failed: 'gl-badge-red', cancelled: 'gl-badge-gray' };
      return m[s] || 'gl-badge-gray';
    },
    providerBadgeClass(p) {
      const m = { inhouse: 'gl-badge-teal', lalamove: 'gl-badge-yellow', grab: 'gl-badge-green' };
      return m[p] || 'gl-badge-gray';
    }
  }
};
</script>

<style>
.gl-page { padding: 24px; max-width: 1400px; }
.gl-page-header { display: flex; align-items: flex-start; justify-content: space-between; margin-bottom: 24px; gap: 16px; flex-wrap: wrap; }
.gl-page-title { font-size: 22px; font-weight: 700; color: #111827; margin: 0 0 2px; letter-spacing: -.3px; }
.gl-page-sub { font-size: 13px; color: #6b7f7b; margin: 0; }
.gl-page-header-actions { display: flex; gap: 8px; flex-shrink: 0; }
.gl-section-label { font-size: 11px; font-weight: 700; color: #9ca3af; text-transform: uppercase; letter-spacing: .7px; margin-bottom: 10px; }
.gl-btn-primary { display: inline-flex; align-items: center; gap: 6px; background: #900C27; color: #fff; border: none; border-radius: 10px; padding: 9px 16px; font-size: 13.5px; font-weight: 600; cursor: pointer; text-decoration: none; white-space: nowrap; }
.gl-btn-primary:hover { background: #1a5048; color: #fff; }
.gl-btn-sm { padding: 6px 12px; font-size: 12.5px; }
.gl-btn-ghost { display: inline-flex; align-items: center; gap: 6px; background: transparent; color: #374151; border: 1.5px solid #e0e7e5; border-radius: 10px; padding: 9px 14px; font-size: 13.5px; font-weight: 500; cursor: pointer; text-decoration: none; white-space: nowrap; }
.gl-btn-ghost:hover { border-color: #900C27; color: #900C27; }
.gl-btn-outline-full { display: flex; align-items: center; justify-content: center; gap: 6px; padding: 9px; border: 1.5px solid #e0e7e5; border-radius: 10px; color: #374151; font-size: 13px; font-weight: 500; text-decoration: none; margin-top: 14px; transition: all .15s; }
.gl-btn-outline-full:hover { border-color: #900C27; color: #900C27; }
.gl-stats-grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 14px; margin-bottom: 8px; }
.gl-stat-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; padding: 18px 18px; display: flex; align-items: center; gap: 14px; }
.gl-stat-icon-wrap { width: 44px; height: 44px; border-radius: 10px; display: flex; align-items: center; justify-content: center; flex-shrink: 0; }
.gl-stat-body { flex: 1; min-width: 0; }
.gl-stat-label { font-size: 11.5px; color: #6b7f7b; font-weight: 500; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 3px; }
.gl-stat-value { font-size: 22px; font-weight: 700; color: #111827; letter-spacing: -.5px; line-height: 1.1; }
.gl-stat-sub { font-size: 11.5px; margin-top: 3px; }
.gl-sub-green { color: #16a34a; font-weight: 500; }
.gl-sub-muted { color: #9ca3af; }
.gl-sub-warn { color: #d97706; font-weight: 500; }
.gl-dash-bottom { display: grid; grid-template-columns: 300px 1fr; gap: 16px; margin-top: 20px; }
.gl-dash-left { display: flex; flex-direction: column; gap: 16px; }
.gl-dash-right {}
.gl-card { background: #fff; border: 1px solid #eaedec; border-radius: 14px; overflow: hidden; }
.gl-card-header { display: flex; align-items: center; justify-content: space-between; padding: 16px 20px; border-bottom: 1px solid #f0f3f2; }
.gl-card-title { font-size: 14px; font-weight: 700; color: #111827; }
.gl-card-sub { font-size: 11.5px; color: #9ca3af; }
.gl-card-body { padding: 16px 20px; }
.gl-provider-row { display: flex; align-items: center; justify-content: space-between; padding: 8px 0; border-bottom: 1px solid #f5f7f6; }
.gl-provider-row:last-child { border-bottom: none; }
.gl-provider-info { display: flex; align-items: center; gap: 8px; }
.gl-provider-count { font-size: 12px; color: #9ca3af; }
.gl-provider-fee { font-size: 13px; font-weight: 600; color: #111827; }
.gl-settings-rows { display: flex; flex-direction: column; gap: 10px; }
.gl-settings-row { display: flex; align-items: center; justify-content: space-between; gap: 8px; }
.gl-settings-row-col { flex-direction: column; align-items: flex-start; }
.gl-settings-key { font-size: 12.5px; color: #6b7f7b; font-weight: 500; }
.gl-provider-badges { display: flex; gap: 5px; flex-wrap: wrap; margin-top: 4px; }
.gl-empty-inline { text-align: center; color: #9ca3af; font-size: 13px; padding: 20px; }
.gl-id-badge { font-family: 'SF Mono', monospace; font-size: 12px; font-weight: 700; color: #900C27; background: #e8f4f1; padding: 2px 6px; border-radius: 5px; }
.gl-badge { display: inline-flex; align-items: center; padding: 3px 9px; border-radius: 20px; font-size: 11.5px; font-weight: 600; text-transform: capitalize; white-space: nowrap; }
.gl-badge-green { background: #dcfce7; color: #15803d; }
.gl-badge-gray { background: #f1f5f9; color: #64748b; }
.gl-badge-red { background: #fee2e2; color: #dc2626; }
.gl-badge-yellow { background: #fef9c3; color: #92400e; }
.gl-badge-info { background: #cffafe; color: #0e7490; }
.gl-badge-blue { background: #dbeafe; color: #1d4ed8; }
.gl-badge-teal { background: #e8f4f1; color: #900C27; }
.gl-tbl-scroll { overflow-x: auto; }
.gl-tbl { width: 100%; border-collapse: collapse; }
.gl-tbl th { padding: 10px 16px; font-size: 11px; font-weight: 600; color: #6b7f7b; text-transform: uppercase; letter-spacing: .6px; background: #f8faf9; border-bottom: 1px solid #eaedec; white-space: nowrap; }
.gl-tbl td { padding: 12px 16px; font-size: 13.5px; color: #111827; border-bottom: 1px solid #f5f7f6; vertical-align: middle; }
.gl-tbl tbody tr:last-child td { border-bottom: none; }
.gl-tbl tbody tr:hover td { background: #f8faf9; }
.gl-tbl-sub { font-size: 11.5px; color: #9ca3af; }
.gl-skel { background: linear-gradient(90deg, #f0f3f2 25%, #e8edeb 50%, #f0f3f2 75%); background-size: 200% 100%; border-radius: 6px; animation: gl-shimmer 1.5s infinite; display: block; }
@keyframes gl-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
.gl-skel-sm { width: 60px; height: 10px; }
.gl-skel-md { width: 100px; height: 10px; }
.gl-skel-lg { width: 140px; height: 10px; }
.gl-skel-badge { width: 60px; height: 22px; border-radius: 20px; background: #f0f3f2; animation: gl-shimmer 1.5s infinite; }
.gl-tbl-skel { padding: 0 4px; }
.gl-tbl-skel-head { display: flex; gap: 20px; padding: 12px 16px; border-bottom: 1px solid #eaedec; }
.gl-tbl-skel-row { display: flex; align-items: center; gap: 20px; padding: 14px 16px; border-bottom: 1px solid #f5f7f6; }
.gl-card-skeleton { display: flex; flex-direction: column; gap: 12px; }
@media (max-width: 1100px) { .gl-dash-bottom { grid-template-columns: 1fr; } }
@media (max-width: 767px) {
  .gl-page { padding: 16px; }
  .gl-page-title { font-size: 18px; }
  .gl-stats-grid { grid-template-columns: repeat(2, 1fr); gap: 10px; }
  .gl-stat-card { padding: 14px; gap: 10px; }
  .gl-stat-value { font-size: 18px; }
  .gl-page-header-actions { width: 100%; }
  .gl-page-header-actions .gl-btn-ghost,
  .gl-page-header-actions .gl-btn-primary { flex: 1; justify-content: center; }
}
body.dark-only .gl-page-title { color: #f1f5f9; }
body.dark-only .gl-page-sub { color: #94a3b8; }
body.dark-only .gl-stat-card,
body.dark-only .gl-card { background: #1e2a27; border-color: #2d3f3b; }
body.dark-only .gl-stat-value { color: #f1f5f9; }
body.dark-only .gl-tbl th { background: #162220; color: #94a3b8; border-color: #2d3f3b; }
body.dark-only .gl-tbl td { color: #e2e8f0; border-color: #1e2a27; }
body.dark-only .gl-tbl tbody tr:hover td { background: #162220; }
body.dark-only .gl-card-header { border-color: #2d3f3b; }
body.dark-only .gl-card-title { color: #f1f5f9; }
body.dark-only .gl-provider-row { border-color: #2d3f3b; }
body.dark-only .gl-provider-fee { color: #f1f5f9; }
body.dark-only .gl-btn-ghost { background: #1e2a27; border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-btn-outline-full { border-color: #2d3f3b; color: #94a3b8; }
body.dark-only .gl-badge-green { background: rgba(22,163,74,.2); color: #4ade80; }
body.dark-only .gl-badge-gray { background: rgba(100,116,139,.2); color: #94a3b8; }
body.dark-only .gl-badge-red { background: rgba(220,38,38,.2); color: #f87171; }
body.dark-only .gl-badge-yellow { background: rgba(217,119,6,.2); color: #fbbf24; }
body.dark-only .gl-badge-info { background: rgba(14,116,144,.2); color: #67e8f9; }
body.dark-only .gl-badge-blue { background: rgba(37,99,235,.2); color: #60a5fa; }
body.dark-only .gl-badge-teal { background: rgba(144,12,39,.2); color: #4ade80; }
body.dark-only .gl-id-badge { background: rgba(144,12,39,.3); color: #5eead4; }
</style>
